import { loadOpenCv, type CvMat, type CvMatVector, type OpenCv } from './opencv';

/**
 * Обработка снимка документа — то, что на Android делает сканер ML Kit.
 *
 * Три действия подряд, каждое со своей причиной:
 *
 *  1. **Поиск краёв листа.** Юрист снимает документ на столе, в кадр попадают край
 *     стола, папка и рука. Лист надо найти и всё остальное отрезать.
 *  2. **Перспективное выпрямление.** Снимок делается под углом — всегда, а не иногда:
 *     держать телефон строго над листом неудобно. Трапеция разворачивается
 *     в прямоугольник.
 *  3. **Адаптивная бинаризация.** Тень от руки и неровный свет лампы делают половину
 *     страницы серой. Порог считается по окрестности каждой точки, а не по всему кадру,
 *     иначе тёмная половина уходит в чёрное целиком.
 *
 * Живой рамки в видоискателе не будет ([WEB_ROADMAP.md](docs/WEB_ROADMAP.md)):
 * для юриста разница в том, что рамка появляется **после** снимка, а не до.
 */

/** Угол листа в координатах исходного снимка. */
export interface Corner {
  x: number;
  y: number;
}

/** Четыре угла по часовой стрелке, начиная с левого верхнего. */
export type Quad = [Corner, Corner, Corner, Corner];

/**
 * Кадр, уменьшенный для поиска краёв.
 *
 * Искать контур на двенадцати мегапикселях незачем: край листа виден и на восьмистах
 * точках по длинной стороне, а времени это занимает в двадцать раз меньше — на старом
 * iPhone разница между «сразу» и «подождите».
 */
const SEARCH_SIDE = 800;

/** Сторона выпрямленной страницы: мелкий шрифт скана остаётся читаемым. */
const OUTPUT_SIDE = 2200;

/** Лист занимает заметную часть кадра: пятна мельче — это не документ. */
const MIN_AREA_SHARE = 0.15;

/**
 * Пороги Canny, которыми пробуем найти края, — от слабого к строгому.
 *
 * Одной пары не хватает, и это главная причина жалобы «границы листа найти не смог,
 * включил свет — ничего не поменялось». При тусклом свете граница белого листа
 * на светлом столе даёт перепад слабее строгого порога: контур рвётся, замкнутого
 * четырёхугольника не получается, и дело не в лампе — при любом освещении результат
 * один и тот же. Слабый порог ловит такую границу, строгий — не тонет в шуме
 * на контрастном снимке. Берём лучшее из всех проб, а не первое попавшееся.
 */
const CANNY_STEPS: [low: number, high: number][] = [
  [20, 60],
  [40, 120],
  [60, 180],
];

/**
 * Допуски упрощения контура — доля периметра.
 *
 * Строгий держит настоящий прямоугольник, свободный прощает волну по краю листа
 * и загнутый угол подшивки — на снятом томе это обычное дело.
 */
const APPROX_STEPS = [0.02, 0.03, 0.05];

/** Окно адаптивного порога: примерно толщина буквы на снимке страницы. */
const THRESHOLD_BLOCK = 25;
const THRESHOLD_SHIFT = 12;

export interface ScannedPage {
  /** Готовая страница: выпрямленная и вычищенная. */
  content: Blob;
  /** Углы, по которым выпрямляли, — их показывает и правит экран. */
  corners: Quad;
  /** Нашли ли лист сами. Нет — углы взяты по краям кадра, и правка нужнее. */
  detected: boolean;
}

/**
 * Ищет лист на снимке.
 *
 * Возвращает углы **в координатах исходного снимка**: экран правки показывает их
 * поверх кадра в полном размере, и пересчитывать масштаб там было бы негде.
 */
export async function findPage(photo: Blob): Promise<{ corners: Quad; detected: boolean }> {
  const cv = await loadOpenCv();
  const bitmap = await createImageBitmap(photo).catch(() => null);
  if (!bitmap) return { corners: wholeFrame(1, 1), detected: false };

  const full = { width: bitmap.width, height: bitmap.height };
  const scale = Math.min(1, SEARCH_SIDE / Math.max(full.width, full.height));
  const canvas = drawScaled(bitmap, scale);
  bitmap.close();

  const source = cv.imread(canvas);
  const gray = new cv.Mat();
  const blurred = new cv.Mat();

  try {
    cv.cvtColor(source, gray, cv.COLOR_RGBA2GRAY);
    // Небольшое размытие перед поиском краёв: без него шум матрицы даёт сотни
    // ложных контуров, и настоящий край листа теряется среди них.
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0);

    const found = search(cv, blurred, canvas.width * canvas.height);
    if (!found) return { corners: wholeFrame(full.width, full.height), detected: false };

    // Обратно в координаты полного кадра: искали на уменьшенном, показывать будем
    // поверх настоящего снимка.
    const corners = found.map((corner) => ({
      x: corner.x / scale,
      y: corner.y / scale,
    })) as Quad;

    return { corners, detected: true };
  } finally {
    source.delete();
    gray.delete();
    blurred.delete();
  }
}

/**
 * Перебор проб: несколько порогов краёв, у каждого — несколько допусков упрощения.
 *
 * Девять проб на уменьшенном до восьмисот точек кадре — доли секунды, и платит за них
 * тот, кто снял документ. Цена ошибки другая: ненайденный лист заставляет юриста
 * расставлять четыре угла пальцем, стоя в коридоре суда.
 */
export function search(cv: OpenCv, blurred: CvMat, frameArea: number): Quad | null {
  let best: Quad | null = null;
  let bestArea = frameArea * MIN_AREA_SHARE;

  for (const [low, high] of CANNY_STEPS) {
    const edges = new cv.Mat();
    const contours = new cv.MatVector();
    const hierarchy = new cv.Mat();

    try {
      cv.Canny(blurred, edges, low, high);
      cv.findContours(edges, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);

      for (const epsilon of APPROX_STEPS) {
        const found = largestQuad(cv, contours, bestArea, epsilon);
        if (!found) continue;

        best = found.quad;
        bestArea = found.area;
      }
    } finally {
      edges.delete();
      contours.delete();
      hierarchy.delete();
    }
  }

  return best;
}

/**
 * Выпрямляет снимок по четырём углам и чистит освещение.
 *
 * Углы приходят с экрана правки: автопоиск ошибается, и последнее слово всегда
 * за человеком.
 */
export async function flattenPage(photo: Blob, corners: Quad): Promise<Blob | null> {
  const cv = await loadOpenCv();
  const bitmap = await createImageBitmap(photo).catch(() => null);
  if (!bitmap) return null;

  const canvas = drawScaled(bitmap, 1);
  bitmap.close();

  const source = cv.imread(canvas);
  const flattened = new cv.Mat();
  const gray = new cv.Mat();
  const cleaned = new cv.Mat();

  try {
    const ordered = clockwise(corners);
    const width = Math.round(
      Math.max(distance(ordered[0], ordered[1]), distance(ordered[3], ordered[2])),
    );
    const height = Math.round(
      Math.max(distance(ordered[0], ordered[3]), distance(ordered[1], ordered[2])),
    );

    // Страница не растягивается до квадрата: пропорции листа сохраняются, а размер
    // ограничивается сверху — иначе выпрямление раздувает файл на ровном месте.
    const limit = Math.max(width, height);
    const factor = limit > OUTPUT_SIDE ? OUTPUT_SIDE / limit : 1;
    const target = { width: Math.round(width * factor), height: Math.round(height * factor) };

    const from = cv.matFromArray(4, 1, cv.CV_32FC2, [
      ordered[0].x, ordered[0].y,
      ordered[1].x, ordered[1].y,
      ordered[2].x, ordered[2].y,
      ordered[3].x, ordered[3].y,
    ]);
    const to = cv.matFromArray(4, 1, cv.CV_32FC2, [
      0, 0,
      target.width, 0,
      target.width, target.height,
      0, target.height,
    ]);

    const transform = cv.getPerspectiveTransform(from, to);
    cv.warpPerspective(source, flattened, transform, new cv.Size(target.width, target.height));
    from.delete();
    to.delete();
    transform.delete();

    cv.cvtColor(flattened, gray, cv.COLOR_RGBA2GRAY);
    cv.adaptiveThreshold(
      gray,
      cleaned,
      255,
      cv.ADAPTIVE_THRESH_GAUSSIAN_C,
      cv.THRESH_BINARY,
      THRESHOLD_BLOCK,
      THRESHOLD_SHIFT,
    );

    const out = document.createElement('canvas');
    cv.imshow(out, cleaned);

    return await new Promise<Blob | null>((resolve) =>
      out.toBlob(resolve, 'image/jpeg', 0.9),
    );
  } finally {
    source.delete();
    flattened.delete();
    gray.delete();
    cleaned.delete();
  }
}

/**
 * Самый большой четырёхугольник среди контуров — это и есть лист.
 *
 * `minArea` приходит снаружи: перебор проб держит лучшее найденное, и каждая
 * следующая проба обязана его превзойти, а не просто дать хоть какой-то результат.
 */
function largestQuad(
  cv: OpenCv,
  contours: CvMatVector,
  minArea: number,
  epsilon: number,
): { quad: Quad; area: number } | null {
  let best: Quad | null = null;
  let bestArea = minArea;

  for (let index = 0; index < contours.size(); index += 1) {
    const contour = contours.get(index);
    const approx = new cv.Mat();

    try {
      // Контур упрощается до многоугольника: у листа после этого ровно четыре угла,
      // у стола и папки — обычно больше или меньше.
      const perimeter = cv.arcLength(contour, true);
      cv.approxPolyDP(contour, approx, epsilon * perimeter, true);
      if (approx.rows !== 4) continue;

      const area = Math.abs(cv.contourArea(approx));
      if (area <= bestArea) continue;
      if (!cv.isContourConvex(approx)) continue;

      bestArea = area;
      best = [0, 1, 2, 3].map((corner) => ({
        x: approx.data32S[corner * 2] ?? 0,
        y: approx.data32S[corner * 2 + 1] ?? 0,
      })) as Quad;
    } finally {
      approx.delete();
    }
  }

  return best ? { quad: clockwise(best), area: bestArea } : null;
}

/**
 * Размер снимка без всякого OpenCV.
 *
 * Нужен тому, у кого обработка вообще не поднялась: рамку по краю кадра нельзя
 * построить, не зная, где этот край.
 */
export async function photoSize(photo: Blob): Promise<{ width: number; height: number } | null> {
  const bitmap = await createImageBitmap(photo).catch(() => null);
  if (!bitmap) return null;

  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();

  return size;
}

/** Углы кадра целиком: автопоиск не нашёл лист, правим руками от края. */
function wholeFrame(width: number, height: number): Quad {
  return [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ];
}

/**
 * Упорядочивает углы: левый верхний, правый верхний, правый нижний, левый нижний.
 *
 * Без этого выпрямление зеркалит и переворачивает страницу: `approxPolyDP` отдаёт
 * углы в том порядке, в каком обошёл контур, а он зависит от того, где начался.
 */
export function clockwise(corners: Quad): Quad {
  const sum = (corner: Corner) => corner.x + corner.y;
  const diff = (corner: Corner) => corner.y - corner.x;
  const all = [...corners];

  const topLeft = all.reduce((a, b) => (sum(a) <= sum(b) ? a : b));
  const bottomRight = all.reduce((a, b) => (sum(a) >= sum(b) ? a : b));
  const topRight = all.reduce((a, b) => (diff(a) <= diff(b) ? a : b));
  const bottomLeft = all.reduce((a, b) => (diff(a) >= diff(b) ? a : b));

  return [topLeft, topRight, bottomRight, bottomLeft];
}

const distance = (from: Corner, to: Corner) => Math.hypot(to.x - from.x, to.y - from.y);

function drawScaled(bitmap: ImageBitmap, scale: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}
